'use client';

import { useTranslation } from 'react-i18next';
import {
  useCurrentUser,
  useMicroSource,
  type MicroContentItem,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { ContentRail } from '../ui/ContentRail';
import { MicroItemCard } from './_MicroItemCard';
import { useMicroCardDecorations } from './_useMicroCardDecorations';

/**
 * User's favorited micro items. Sourced from
 * {@code MicroSource.favorites.list}. Signed-out users see nothing
 * (the favorites surface presumes auth).
 *
 * <p>{@code variant}: {@code 'rail'} (default) for the capped
 * quick-glance row on the landing; {@code 'grid'} for the full
 * favorites page — an unbounded responsive grid, matching legacy's
 * /micro/favorites.
 */
export interface MicroFavoritesRailProps {
  title?: string;
  itemHrefTemplate?: string;
  variant?: 'rail' | 'grid';
  /** Show the content-kind chip over each card's cover. */
  showKindBadge?: boolean;
}

export function MicroFavoritesRail({
  title: authoredTitle,
  itemHrefTemplate,
  variant = 'rail',
  showKindBadge = true,
}: MicroFavoritesRailProps) {
  const { t } = useTranslation('site-builder-blocks');
  const title = authoredTitle ?? t('microFavorites.title');
  const user = useCurrentUser<{ name?: string }>();
  const micro = useMicroSource();
  const decorations = useMicroCardDecorations();

  const state = useAsyncResource<MicroContentItem[]>(
    async (signal) => {
      if (!user) return null;
      if (!micro?.favorites) return null;
      const items = await micro.favorites.list(signal);
      return items.length > 0 ? items : null;
    },
    [user, micro],
  );

  if (!user) return null;

  if (!micro?.favorites) {
    return (
      <SectionShell title={title}>
        <AdapterPlaceholder blockType="MicroFavoritesRail" adapterName="MicroSource.favorites" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={title}>
        <SkeletonRow count={6} layout={variant === 'grid' ? 'grid' : 'rail'} />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={title}>
        <EmptyState title={t('microFavorites.empty.title')} hint={t('microFavorites.empty.hint')} />
      </SectionShell>
    );
  }

  const cards = state.data.map((item) => (
    <MicroItemCard
      showKindBadge={showKindBadge}
      key={item.id}
      item={item}
      // Everything this block lists IS a favorite — don't wait for the
      // fetchMine round-trip to fill the hearts.
      favorite
      progress={decorations.progressFor(item.id)}
      categoryName={decorations.categoryNameFor(item.categoryId)}
      {...(itemHrefTemplate ? { hrefTemplate: itemHrefTemplate } : {})}
    />
  ));

  if (variant === 'grid') {
    return (
      <SectionShell title={title}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))',
            gap: 'clamp(12px, 2vw, 16px)',
          }}
        >
          {cards}
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell title={title}>
      <ContentRail ariaLabel={title} itemWidth="240px" snap="start">
        {cards}
      </ContentRail>
    </SectionShell>
  );
}
